import {
    Box,
    List,
    ListItemText,
    Skeleton,
    Typography,
} from '@mui/material'
import { Features } from 'c1gen/features'
import { Roles } from 'c1gen/roles'
import { EmptyList } from 'components/core/empty-list/'
import { C1Icon } from 'components/core/icons/C1Icon'
import { navigationConfig } from 'components/modular-nav/config/navigation'
import { settingsNavigation } from 'components/modular-nav/config/settingsNavigation'
import { NavContainer, NavModule } from 'components/modular-nav/types'
import { useHasBuiltinRole, useRoleChecker, useUnsafeFeatureChecker } from 'components/no-ui/capability'
import { C1AccessReviewDisplayNameWithFetch } from 'components/pages/accessreview/display-name'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { C1PolicyDisplayNameWithFetch } from 'components/pages/policy/display-name'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useLocalStorage } from 'hooks/local-storage/index'
import { RoleID } from 'lib/constants/system'
import { ChevronRight, LucideIcon } from 'lucide-react'
import { AccessReviewRef } from 'pbts/c1/api/accessreview/v1/accessreview'
import { AppRef } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { PolicyRef } from 'pbts/c1/api/policy/v1/policy'
import { UserRef } from 'pbts/c1/api/user/v1/user'
import { FC, ReactNode, useMemo, useEffect, MutableRefObject } from 'react'
import { FormattedMessage } from 'react-intl'
import { appMultiGetApi } from 'redux_toolkit/apps'
import { getUserMultiApi } from 'redux_toolkit/users'
import { SectionHeader } from './SectionHeader'
import { SpotlightResultItem } from './SpotlightResultItem'

export const SPOTLIGHT_SEARCH_STORAGE_KEY = 'c1-spotlight-search-v2'

/**
 * Stored recent search item - discriminated union by category.
 * Each variant stores the ref needed to fetch/render at display time.
 */
export type SearchResult =
    | { category: 'page'; spotlightId: string }
    | { category: 'user'; ref: UserRef }
    | { category: 'app'; ref: AppRef }
    | { category: 'entitlement'; ref: AppEntitlementRef }
    | { category: 'campaign'; ref: AccessReviewRef }
    | { category: 'policy'; ref: PolicyRef }

/** Extract category type from SearchResult */
type SearchResultCategory = SearchResult['category']

/** Category display order */
const categoryOrder: SearchResultCategory[] = ['page', 'app', 'entitlement', 'user', 'policy', 'campaign']

/** Category labels for section headers (internationalized) */
const categoryFormattedMessages: Record<SearchResultCategory, ReactNode> = {
    page: <FormattedMessage id="spotlight_pages" defaultMessage="Pages" />,
    app: <FormattedMessage id="spotlight_apps" defaultMessage="Apps" />,
    entitlement: <FormattedMessage id="spotlight_entitlements" defaultMessage="Entitlements" />,
    user: <FormattedMessage id="spotlight_users" defaultMessage="Users" />,
    policy: <FormattedMessage id="spotlight_policies" defaultMessage="Policies" />,
    campaign: <FormattedMessage id="spotlight_campaigns" defaultMessage="Campaigns" />,
}

/** Get unique key for a SearchResult, including category to ensure uniqueness */
const getSearchResultKey = (item: SearchResult): string => {
    switch (item.category) {
        case 'page':
            return `page-${item.spotlightId}`
        case 'entitlement':
            return `entitlement-${item.ref.appId}-${item.ref.id}`
        case 'user':
        case 'app':
        case 'campaign':
        case 'policy':
            return `${item.category}-${item.ref.id}`
        default:
            return ''
    }
}

export const getUpdatedRecentSearch = (item: SearchResult, existing: Array<SearchResult> | null | undefined): Array<SearchResult> => {
    if (!existing || existing.length === 0) {
        return [item]
    }
    // Remove if already exists, then add to top
    const itemKey = getSearchResultKey(item)
    const filtered = existing.filter((e) => getSearchResultKey(e) !== itemKey)
    return [item, ...filtered].slice(0, 5)
}

/**
 * Searchable item for spotlight search results
 */
export type SearchableSpotlightItem = {
    /** Unique identifier */
    id: string
    /** Display text (ReactNode) */
    text: ReactNode
    /** Plain text for search matching */
    searchText: string
    /** Icon component */
    icon: LucideIcon
    /** Path to navigate to */
    pathname: string
    /** Section/category label for breadcrumb display */
    section?: ReactNode
    /** Plain text section for search matching */
    sectionSearchText?: string
    /** Required roles */
    roles?: Roles[]
    /** Required features */
    features?: Features[]
}

/**
 * Hook to get all searchable spotlight items (navigation pages).
 * Items are filtered by role/feature permissions.
 */
export const useSearchableSpotlightItems = (): SearchableSpotlightItem[] => {
    const hasRole = useRoleChecker()
    const hasFeature = useUnsafeFeatureChecker()
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)

    return useMemo(() => {
        const items: SearchableSpotlightItem[] = []

        // Helper to check if a module is visible based on roles/features
        const isModuleVisible = (navModule: NavModule): boolean => {
            const meetsRole = isReadOnlySuperAdmin || !navModule.roles?.length || navModule.roles.some(hasRole)
            const meetsFeature = navModule.features?.length ? navModule.features.some(hasFeature) : true
            const noHideFeature = navModule.hideOnFeatures?.length
                ? !navModule.hideOnFeatures.some(hasFeature)
                : true
            return meetsRole && meetsFeature && noHideFeature && !navModule.placeholder
        }

        // Helper to check if a container is visible
        const isContainerVisible = (container: NavContainer): boolean => {
            const containerMeetsRole = isReadOnlySuperAdmin || !container.roles?.length || container.roles.some(hasRole)
            const containerMeetsFeature = !container.features?.length || container.features.some(hasFeature)
            return containerMeetsRole && containerMeetsFeature
        }

        // Add navigation pages from main nav config
        for (const container of navigationConfig) {
            if (!isContainerVisible(container)) continue

            // Get container label text for search
            const containerSearchText = container.id.replace(/-/g, ' ')

            for (const navModule of container.modules) {
                if (!isModuleVisible(navModule)) continue

                const moduleSearchText = navModule.id.replace(/-/g, ' ')

                items.push({
                    id: `page-${container.id}-${navModule.id}`,
                    text: navModule.label,
                    searchText: `${moduleSearchText} ${containerSearchText}`.toLowerCase(),
                    icon: navModule.icon,
                    pathname: navModule.pathname,
                    section: container.label,
                    sectionSearchText: containerSearchText.toLowerCase(),
                    roles: navModule.roles,
                    features: navModule.features,
                })
            }

            // Handle containers with defaultPath but no modules
            if (container.modules.length === 0 && container.defaultPath) {
                items.push({
                    id: `page-${container.id}`,
                    text: container.label,
                    searchText: containerSearchText.toLowerCase(),
                    icon: container.icon,
                    pathname: container.defaultPath,
                    section: container.label,
                    sectionSearchText: containerSearchText.toLowerCase(),
                    roles: container.roles,
                    features: container.features,
                })
            }
        }

        // Add settings navigation pages
        for (const group of settingsNavigation) {
            const groupSearchText = group.id.replace(/-/g, ' ')

            for (const item of group.items) {
                if (!isModuleVisible(item)) continue

                const itemSearchText = item.id.replace(/-/g, ' ')

                items.push({
                    id: `page-settings-${group.id}-${item.id}`,
                    text: item.label,
                    searchText: `${itemSearchText} settings ${groupSearchText}`.toLowerCase(),
                    icon: item.icon,
                    pathname: item.pathname,
                    section: (
                        <>
                            <FormattedMessage id="nav_settings" defaultMessage="Settings" />
                            <C1Icon icon={ChevronRight} size="sm" sx={{ color: 'text.secondary', mx: 1, verticalAlign: 'middle' }} />
                            {group.label}
                        </>
                    ),
                    sectionSearchText: `settings ${groupSearchText}`.toLowerCase(),
                    roles: item.roles,
                    features: item.features,
                })
            }
        }

        return items
    }, [hasRole, hasFeature, isReadOnlySuperAdmin])
}

type RecentSearchesProps = {
    goTo: (path: string) => void
    selectedIndex: number
    totalResultsRef: MutableRefObject<number>
    activateSelectedRef: MutableRefObject<(() => void) | null>
}

/** Renders a recent spotlight item (page) by looking it up from config */
const RecentSpotlightItem: FC<{
    spotlightItem: SearchableSpotlightItem
    onClick: () => void
    selected?: boolean
}> = ({ spotlightItem, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <ListItemText>
            <Typography sx={{ display: 'flex', alignItems: 'center' }} noWrap>
                <C1Icon icon={spotlightItem.icon} size="sm" sx={{ mr: 1 }} />
                {spotlightItem.section && (
                    <>
                        <Typography component="span" color="text.secondary">{spotlightItem.section}</Typography>
                        <C1Icon icon={ChevronRight} size="sm" sx={{ color: 'text.secondary', mx: 1 }} />
                    </>
                )}
                <Typography component="span">{spotlightItem.text}</Typography>
            </Typography>
        </ListItemText>
    </SpotlightResultItem>

// No-op for loading state click handlers
const noop = (): void => undefined

/** Renders a recent user by fetching fresh data */
const RecentUserItem: FC<{ id: string; goTo: (path: string) => void; selected?: boolean }> = ({ id, goTo, selected }) => {
    const { data, isLoading } = getUserMultiApi.useMultiGet(UserRef.create({ id }))
    if (isLoading || !data) {
        return (
            <SpotlightResultItem onClick={noop} selected={selected}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton variant="text" width={120} />
            </SpotlightResultItem>
        )
    }
    return (
        <SpotlightResultItem onClick={(): void => goTo(`/admin/directory/users/${id}`)} selected={selected}>
            <UserDisplayName user={data} readOnly margin={false} />
        </SpotlightResultItem>
    )
}

/** Renders a recent app by fetching fresh data */
const RecentAppItem: FC<{ id: string; goTo: (path: string) => void; selected?: boolean }> = ({ id, goTo, selected }) => {
    const { data, isLoading } = appMultiGetApi.useMultiGet(AppRef.create({ id }))
    if (isLoading || !data) {
        return (
            <SpotlightResultItem onClick={noop} selected={selected}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton variant="text" width={120} />
            </SpotlightResultItem>
        )
    }
    return (
        <SpotlightResultItem onClick={(): void => goTo(`/admin/applications/${id}`)} selected={selected}>
            <AppDisplayName app={data} readOnly avatarSize="md" margin={false} />
        </SpotlightResultItem>
    )
}

/** Renders a recent entitlement using the WithFetch component */
const RecentEntitlementItem: FC<{
    id: string
    appId: string
    goTo: (path: string) => void
    selected?: boolean
}> = ({ id, appId, goTo, selected }) =>
    <SpotlightResultItem onClick={(): void => goTo(`/admin/applications/${appId}/entitlements/${id}`)} selected={selected}>
        <C1EntitlementDisplayNameWithFetch entitlementId={id} appId={appId} readOnly />
    </SpotlightResultItem>

/** Renders a recent campaign using C1AccessReviewDisplayNameWithFetch */
const RecentCampaignItem: FC<{
    id: string
    goTo: (path: string) => void
    selected?: boolean
}> = ({ id, goTo, selected }) =>
    <SpotlightResultItem onClick={(): void => goTo(`/admin/campaigns/${id}`)} selected={selected}>
        <C1AccessReviewDisplayNameWithFetch accessReviewId={id} readOnly />
    </SpotlightResultItem>

/** Renders a recent policy using C1PolicyDisplayNameWithFetch */
const RecentPolicyItem: FC<{
    id: string
    goTo: (path: string) => void
    selected?: boolean
}> = ({ id, goTo, selected }) =>
    <SpotlightResultItem onClick={(): void => goTo(`/admin/settings/policies/${id}`)} selected={selected}>
        <C1PolicyDisplayNameWithFetch policyId={id} readOnly />
    </SpotlightResultItem>

export const RecentSearches: FC<RecentSearchesProps> = ({ goTo, selectedIndex, totalResultsRef, activateSelectedRef }) => {
    const [recentSearches, saveRecentSearches] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])
    const spotlightItems = useSearchableSpotlightItems()

    // Create a map of spotlight items by ID for quick lookup
    const spotlightItemsById = useMemo(() => {
        const map = new Map<string, SearchableSpotlightItem>()
        for (const item of spotlightItems) {
            map.set(item.id, item)
        }
        return map
    }, [spotlightItems])

    // Type guard to validate SearchResult shape from localStorage
    const isValidSearchResult = (item: unknown): item is SearchResult => {
        if (!item || typeof item !== 'object') return false
        const obj = item as Record<string, unknown>
        const ref = obj.ref as Record<string, unknown> | undefined
        switch (obj.category) {
            case 'page':
                return typeof obj.spotlightId === 'string' && spotlightItemsById.has(obj.spotlightId)
            case 'user':
            case 'app':
            case 'campaign':
            case 'policy':
                return ref !== undefined && typeof ref.id === 'string'
            case 'entitlement':
                return ref !== undefined && typeof ref.id === 'string' && typeof ref.appId === 'string'
            default:
                return false
        }
    }

    // Filter to only valid items and clear invalid ones from cache
    const validRecents = useMemo(() => {
        if (!recentSearches) return []
        const valid = recentSearches.filter(isValidSearchResult)
        // If we filtered out invalid items, update the cache
        if (valid.length !== recentSearches.length) {
            saveRecentSearches(valid)
        }
        return valid
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recentSearches, spotlightItemsById])

    const hasRecents = validRecents.length > 0

    // Build flat list of items with paths for keyboard navigation (follows categoryOrder for visual consistency)
    const allItems = useMemo(() => {
        const items: Array<{ key: string; path: string }> = []
        const recents = validRecents.slice(0, 5)

        // Iterate in categoryOrder to match rendering order
        for (const category of categoryOrder) {
            for (const item of recents) {
                if (item.category !== category) continue

                switch (item.category) {
                    case 'page': {
                        const spotlightItem = spotlightItemsById.get(item.spotlightId)
                        if (spotlightItem) {
                            items.push({ key: getSearchResultKey(item), path: spotlightItem.pathname })
                        }
                        break
                    }
                    case 'user':
                        items.push({ key: getSearchResultKey(item), path: `/admin/directory/users/${item.ref.id}` })
                        break
                    case 'app':
                        items.push({ key: getSearchResultKey(item), path: `/admin/applications/${item.ref.id}` })
                        break
                    case 'entitlement':
                        items.push({ key: getSearchResultKey(item), path: `/admin/applications/${item.ref.appId}/entitlements/${item.ref.id}` })
                        break
                    case 'campaign':
                        items.push({ key: getSearchResultKey(item), path: `/admin/campaigns/${item.ref.id}` })
                        break
                    case 'policy':
                        items.push({ key: getSearchResultKey(item), path: `/admin/settings/policies/${item.ref.id}` })
                        break
                    default:
                        break
                }
            }
        }
        return items
    }, [validRecents, spotlightItemsById])

    // Update total results ref
    useEffect(() => {
        totalResultsRef.current = allItems.length
    }, [allItems.length, totalResultsRef])

    // Update activate selected ref
    useEffect(() => {
        activateSelectedRef.current = (): void => {
            if (selectedIndex >= 0 && selectedIndex < allItems.length) {
                goTo(allItems[selectedIndex].path)
            }
        }
    }, [selectedIndex, allItems, goTo, activateSelectedRef])

    // Create a map from key to index for quick lookup during render
    const keyToIndex = useMemo(() => {
        const map = new Map<string, number>()
        allItems.forEach((item, idx) => {
            map.set(item.key, idx)
        })
        return map
    }, [allItems])

    // Group recents by category for display
    const groupedRecents = useMemo(() => {
        const groups: Partial<Record<SearchResultCategory, SearchResult[]>> = {}
        for (const item of validRecents.slice(0, 5)) {
            const categoryGroup = groups[item.category]
            if (categoryGroup) {
                categoryGroup.push(item)
            } else {
                groups[item.category] = [item]
            }
        }
        return groups
    }, [validRecents])

    // Helper to render an item
    const renderItem = (item: SearchResult): React.ReactNode => {
        const key = getSearchResultKey(item)
        const itemIndex = keyToIndex.get(key) ?? -1
        const isSelected = itemIndex === selectedIndex

        switch (item.category) {
            case 'page': {
                const spotlightItem = spotlightItemsById.get(item.spotlightId)
                if (!spotlightItem) return null
                return (
                    <RecentSpotlightItem
                        key={key}
                        spotlightItem={spotlightItem}
                        onClick={(): void => goTo(spotlightItem.pathname)}
                        selected={isSelected}
                    />
                )
            }
            case 'user':
                return <RecentUserItem key={key} id={item.ref.id} goTo={goTo} selected={isSelected} />
            case 'app':
                return <RecentAppItem key={key} id={item.ref.id} goTo={goTo} selected={isSelected} />
            case 'entitlement':
                return (
                    <RecentEntitlementItem
                        key={key}
                        id={item.ref.id}
                        appId={item.ref.appId}
                        goTo={goTo}
                        selected={isSelected}
                    />
                )
            case 'campaign':
                return <RecentCampaignItem key={key} id={item.ref.id} goTo={goTo} selected={isSelected} />
            case 'policy':
                return <RecentPolicyItem key={key} id={item.ref.id} goTo={goTo} selected={isSelected} />
            default:
                return null
        }
    }

    return (
        <List disablePadding>
            {hasRecents
                ? (
                    <>
                        <SectionHeader>
                            <FormattedMessage id="recent_searches_heading" defaultMessage="Your recent searches" />
                        </SectionHeader>
                        {categoryOrder.map((category) => {
                            const items = groupedRecents[category]
                            if (!items || items.length === 0) return null

                            return (
                                <div key={category}>
                                    <SectionHeader>{categoryFormattedMessages[category]}</SectionHeader>
                                    {items.map(renderItem)}
                                </div>
                            )
                        })}
                    </>
                )
                : (
                    <Box pt={3}>
                        <EmptyList text={<FormattedMessage id="no_recent_searches" defaultMessage="Your recent searches will appear here." />} />
                    </Box>
                )
            }
        </List>
    )
}
